import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';

//API
import API from '../../../api/api';
import APIPERSONAL from '../../../api/personal/apipersonal';
import { useSnackbar } from 'notistack';
import { Role } from 'src/@types/lms/roles';
import SelectRoleDialog from './LoginSelectRoleDialog';

// ----------------------------------------------------------------------

type FormValuesProps = {
  tenant: string;
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
};

export default function LoginForm() {
  const { enqueueSnackbar } = useSnackbar();
  const { login } = useAuth();

  // const navigate = useNavigate();

  // const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const [tenant, setTenant] = useState('');
  const [preToken, setPreToken] = useState('');
  const [preRefreshToken, setPreRefreshToken] = useState('');

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    tenant: 'root',
    email: '',//'admin@root.com',
    password: '',//'123Pa$$word!',
    remember: true,
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const [roleData, setRoleData] = useState<Role[]>([
    {
      id: '',
      name: '',
      description: '',
      enabled: true,
    },
  ]);

  const [openRoles, setOpenRoles] = useState(false);

  const handleOpenRoles = () => {
    setOpenRoles(true);
  };

  const handleCloseRoles = () => {
    setOpenRoles(false);
  };

  const {
    // reset,
    // setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    let api = new API();
    let dataResp = await api.preLogin({
      tenant: data.tenant,
      email: data.email,
      password: data.password,
    });
    if (dataResp.success) {
      setTenant(data.tenant);
      setPreToken(dataResp.token);
      setPreRefreshToken(dataResp.refreshToken);
      let apipersonal = new APIPERSONAL();
      apipersonal.init();
      let resp = await apipersonal.getLoginRoles(dataResp.token);
      if (resp.length === 0) {
        enqueueSnackbar('You have no roles assigned, please contact your administrator', {
          variant: 'error',
        });
      } else if (resp.length === 1) {
        submitLogin(resp[0].name, data.tenant, dataResp.token, dataResp.refreshToken);
      } else {
        setRoleData(resp);
        handleOpenRoles();
      }
    } else {
      enqueueSnackbar(dataResp.errorMessage, { variant: 'error' });
    }

    // try {
    //   await login(data.tenant, data.email, data.password);
    // } catch (error) {
    //   console.error(error);
    //   reset();
    //   if (isMountedRef.current) {
    //     setError('afterSubmit', { ...error, message: error.messages });
    //   }
    // }
  };

  async function submitLogin(role: string, _tenant: string, _token: string, _refreshToken: string) {
    try {
      let api = new API();
      let dataResp = await api.login({
        tenant: _tenant,
        token: _token,
        refreshToken: _refreshToken,
        roleName: role,
      });
      if (dataResp.success && dataResp.token) {
        //let ref = (window.location.href.includes("login") ||  window.location.href.includes("404"))? PATH_LMS.inspections.list : window.location.href;
        //let ref = window.location.href;
        login(dataResp.token);//.then((e) => (window.location.href = ref));
      } else {
        enqueueSnackbar(dataResp.errorMessage, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  }

  const onSubmitRole = async (data: Role) => {
    if (tenant !== '' && preToken !== '' && preRefreshToken !== '') {
      submitLogin(data.name, tenant, preToken, preRefreshToken);
    }
  };

  useEffect(() => {});

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label="Remember me" />
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
          Forgot password?
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Login
      </LoadingButton>
      <SelectRoleDialog
        values={roleData}
        isOpen={openRoles}
        onClose={handleCloseRoles}
        onSubmitRole={onSubmitRole}
      />
    </FormProvider>
  );
}
